/**
 * Ürün bazlı kârlılık ve devir hızı — KK-606, KK-608.
 *
 * =============================================================================
 * DEVİR HIZI BİR YAKLAŞIKTIR VE ÖYLE ETİKETLENİR
 * -----------------------------------------------------------------------------
 * Sunucu payda olarak ŞU ANKİ sağlam stoğu kullanır; doğru tanım "dönem içi
 * ortalama stok"tur ve günlük stok fotoğrafı gerektirir (MVP dışı). Yanılgıyı
 * gizlemek yerine `mevcut_saglam_stok` sütunu de gösterilir ki kullanıcı neyi
 * böldüğümüzü görsün. Rakamı sahiplenilemez yapan şey, kaynağını gizlemektir.
 *
 * KIRPMA (KK-608): sunucu limit + 1 satır çeker ve `kirpildi` döner. Bu bayrak
 * SESSİZCE YUTULMAZ; listenin üstünde uyarı olarak çıkar.
 * =============================================================================
 */
import { useEffect, useState } from "react";
import {
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { yerelGun } from "@kantin/shared";
import { oturumDurumu } from "../../veri/oturum.js";
import { useOkul, useUrunKarlilik } from "../../veri/sorgular.js";
import { ayinIlkGunu, yerelBugun } from "../../veri/bicim.js";
import {
  DisaAktar,
  FiltreSeridi,
  Sayfa,
  SayfaBasligi,
  SecimSuzgeci,
  SorguDurumu,
  TarihAlani,
} from "../../ortak.js";

const LIMIT_SECENEKLERI = [
  { deger: "100", metin: "100 ürün" },
  { deger: "500", metin: "500 ürün" },
  { deger: "2000", metin: "2000 ürün" },
];

export function UrunKarlilik() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const okul = useOkul();
  const [bas, setBas] = useState(ayinIlkGunu(yerelBugun()));
  const [bit, setBit] = useState(yerelBugun());
  const [limit, setLimit] = useState("500");
  const [dokunuldu, setDokunuldu] = useState(false);

  useEffect(() => {
    if (dokunuldu || !okul.data) return;
    const gun = yerelGun(new Date(), okul.data.saat_dilimi);
    setBas(ayinIlkGunu(gun));
    setBit(gun);
  }, [okul.data, dokunuldu]);

  const rapor = useUrunKarlilik(
    bas,
    bit,
    seciliNoktaId,
    Number.parseInt(limit, 10),
  );
  const satirlar = rapor.data?.satirlar ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Ürün kârlılığı"
        aciklama="Hangi ürün sırtı taşıyor, hangisi rafta bekliyor. Maliyet, satış anındaki alış fiyatıdır."
        eylem={
          <DisaAktar
            rapor="urun-karlilik"
            dosyaKoku={`urun-karlilik-${bas}_${bit}`}
            sorgu={{ bas, bit, nokta_id: seciliNoktaId ?? undefined }}
            devreDisi={!bas || !bit}
          />
        }
      />

      <FiltreSeridi>
        <TarihAlani
          id="uk-bas"
          etiket="Başlangıç"
          deger={bas}
          degistir={(v) => {
            setDokunuldu(true);
            setBas(v);
          }}
        />
        <TarihAlani
          id="uk-bit"
          etiket="Bitiş"
          deger={bit}
          degistir={(v) => {
            setDokunuldu(true);
            setBit(v);
          }}
        />
        <SecimSuzgeci
          id="uk-limit"
          etiket="Satır limiti"
          deger={limit}
          degistir={setLimit}
          secenekler={LIMIT_SECENEKLERI}
        />
        {seciliNoktaId && (
          <Rozet tur="simit">nokta süzgeci sunucuda uygulanıyor</Rozet>
        )}
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={rapor.isLoading}
        hata={rapor.error}
        bos={!rapor.isLoading && satirlar.length === 0}
        bosBaslik="Bu aralıkta satış yok"
        bosAciklama="Seçili dönemde tamamlanmış satış satırı bulunamadı."
        iskeletSatir={6}
        yenile={() => void rapor.refetch()}
      >
        {rapor.data && (
          <>
            {rapor.data.kirpildi && (
              <Uyari tur="uyari" baslik="Liste kırpıldı">
                Sonuç{" "}
                <span className="rakam font-mono">{rapor.data.limit}</span>{" "}
                satır limitine takıldı; liste EKSİKTİR. Tarih aralığını
                daraltın, nokta süzgeci uygulayın veya limiti yükseltin.
              </Uyari>
            )}

            <p className="m-0 text-sm text-murekkep-3">
              Dönem{" "}
              <span className="rakam font-mono">{rapor.data.gun_sayisi}</span>{" "}
              gün. Devir hızı YAKLAŞIKTIR: payda şu anki sağlam stoktur, dönem
              içi ortalama stok değildir.
            </p>

            <Tablo>
              <TabloBaslik>
                <th scope="col">Ürün</th>
                <th scope="col">Kategori</th>
                <th scope="col">Satılan</th>
                <th scope="col">Ciro</th>
                <th scope="col">Maliyet</th>
                <th scope="col">Kâr</th>
                <th scope="col">Marj</th>
                <th scope="col">Ciro payı</th>
                <th scope="col">Günlük ort.</th>
                <th scope="col">Sağlam stok</th>
                <th scope="col">Devir (yaklaşık)</th>
              </TabloBaslik>
              <TabloGovde>
                {satirlar.map((u) => (
                  <tr key={u.urun_id}>
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      {u.urun_ad}
                    </th>
                    <td>{u.kategori_ad ?? "—"}</td>
                    <td className="rakam text-right font-mono">
                      {u.satilan_adet}
                    </td>
                    <td className="text-right">
                      <Tutar deger={u.ciro} yonsuz boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar deger={u.maliyet} yonsuz boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar
                        deger={u.kar}
                        isaretiGoster
                        boy="sm"
                        etiket={`${u.urun_ad} kârı`}
                      />
                    </td>
                    <td className="rakam text-right font-mono">
                      {u.marj_yuzde ? `%${u.marj_yuzde}` : "—"}
                    </td>
                    <td className="rakam text-right font-mono">
                      {u.ciro_payi_yuzde ? `%${u.ciro_payi_yuzde}` : "—"}
                    </td>
                    <td className="rakam text-right font-mono">
                      {u.gunluk_ortalama_adet}
                    </td>
                    <td className="rakam text-right font-mono">
                      {u.mevcut_saglam_stok}
                    </td>
                    <td className="rakam text-right font-mono">
                      {u.devir_hizi ?? "—"}
                    </td>
                  </tr>
                ))}
              </TabloGovde>
            </Tablo>
          </>
        )}
      </SorguDurumu>
    </Sayfa>
  );
}
